/**
 * UserProfilesScreen — Profile-based Privilege Matrix Editor
 *
 * Layout:
 *   Toolbar  : New Profile · Save Profile · Apply to User
 *   Left     : Profile list (Global + Tenant) with scope badges + search
 *   Right    : Privilege matrix (COMMON/SPECIAL) — same design as UserPrivilegesScreen
 *              Vertical column headers (writing-mode: vertical-rl, 12px)
 */
import React, { useState, useEffect, useRef } from 'react';
import { API_BASE, getToken } from '../../lib/api';
import { Plus, Save, Users, Shield, RefreshCw, ChevronDown, ChevronRight, CheckSquare, Square, Trash2, Search } from 'lucide-react';

// ── Operation definitions (identical to UserPrivilegesScreen) ─────────────────
const COMMON_OPS = [
  { key: 'visible', label: 'View',    color: '#6366f1' },
  { key: 'save',    label: 'New',     color: '#10b981' },
  { key: 'update',  label: 'Edit',    color: '#f59e0b' },
  { key: 'delete',  label: 'Delete',  color: '#ef4444' },
  { key: 'print',   label: 'Print',   color: '#8b5cf6' },
  { key: 'preview', label: 'Preview', color: '#06b6d4' },
];

const SPECIAL_OPS = [
  { key: 'export',  label: 'Export',   color: '#0ea5e9' },
  { key: 'import',  label: 'Import',   color: '#65a30d' },
  { key: 'approve', label: 'Approve',  color: '#10b981' },
  { key: 'reject',  label: 'Reject',   color: '#f43f5e' },
  { key: 'cancel',  label: 'Cancel',   color: '#94a3b8' },
  { key: 'email',   label: 'Email',    color: '#ec4899' },
  { key: 'message', label: 'Message',  color: '#22c55e' },
  { key: 'attach',  label: 'Attach',   color: '#a16207' },
  { key: 'ai',      label: 'AI Chat',  color: '#a855f7' },
];

const ALL_OPS  = [...COMMON_OPS, ...SPECIAL_OPS];
const ALL_KEYS = ALL_OPS.map(o => o.key);
const COM_KEYS = COMMON_OPS.map(o => o.key);
const SPL_KEYS = SPECIAL_OPS.map(o => o.key);
const COL_W    = 32;

function authFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(opts.headers || {}) },
  });
}

function allEnabled(screens, keys, privMap) {
  return screens.length > 0 && screens.every(s => keys.every(k => privMap[s.screenId]?.[k]));
}
function anyEnabled(screens, keys, privMap) {
  return screens.some(s => keys.some(k => privMap[s.screenId]?.[k]));
}

// ── Sub-components (scope-limited, no hooks) ──────────────────────────────────

function GrpBtn({ label, checked, partial, color, onClick }) {
  return (
    <button onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 7px', background: checked ? `${color}22` : 'transparent', border: `1px solid ${checked || partial ? color : '#e2e8f0'}`, borderRadius: 4, cursor: 'pointer', fontSize: 9, fontWeight: 700, color: checked || partial ? color : '#94a3b8' }}>
      {checked ? <CheckSquare size={9} /> : partial ? <CheckSquare size={9} style={{ opacity: .55 }} /> : <Square size={9} />}
      {label}
    </button>
  );
}

function MiniBtn({ label, title, checked, partial, color, onClick }) {
  return (
    <button onClick={onClick} title={title}
      style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '1px 5px', background: 'none', border: `1px solid ${checked ? color : '#cbd5e1'}`, borderRadius: 3, cursor: 'pointer', fontSize: 9, color: checked ? color : '#94a3b8' }}>
      {checked ? <CheckSquare size={9} /> : partial ? <CheckSquare size={9} style={{ opacity: .5 }} /> : <Square size={9} />}
      {label}
    </button>
  );
}

// ── Profile Privilege Matrix ───────────────────────────────────────────────────
function ProfilePrivMatrix({ tree, privMap, expandWork, setExpandWork, toggleOp, setGroupForScreens, isDark, panel, bd, tx, mt, rh, cBg, cHd, sBg, sHd }) {
  const TOTAL_W = ALL_OPS.length * COL_W + 2;
  const getOp   = (sid, k) => privMap[sid]?.[k] ?? false;

  // All visible screens for column-header toggles
  const allVisibleScreens = tree.flatMap(m => m.subModules.flatMap(sm => sm.screens));

  return (
    <div style={{ flex: 1, overflow: 'auto' }}>
      {/* Sticky column header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: panel, borderBottom: `2px solid ${bd}` }}>
        {/* Group labels row — with global ALL / NONE buttons */}
        <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${bd}` }}>
          <div style={{ width: 280, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5, padding: '0 10px' }}>
            <span style={{ fontSize: 9, color: mt, fontWeight: 700, letterSpacing: '.06em' }}>ALL SCREENS:</span>
            <GrpBtn label="All" checked={allEnabled(allVisibleScreens, ALL_KEYS, privMap)} partial={anyEnabled(allVisibleScreens, ALL_KEYS, privMap) && !allEnabled(allVisibleScreens, ALL_KEYS, privMap)} color="#6366f1"
              onClick={() => setGroupForScreens(allVisibleScreens, ALL_KEYS, !allEnabled(allVisibleScreens, ALL_KEYS, privMap))} />
            <GrpBtn label="Common" checked={allEnabled(allVisibleScreens, COM_KEYS, privMap)} partial={anyEnabled(allVisibleScreens, COM_KEYS, privMap) && !allEnabled(allVisibleScreens, COM_KEYS, privMap)} color={cHd}
              onClick={() => setGroupForScreens(allVisibleScreens, COM_KEYS, !allEnabled(allVisibleScreens, COM_KEYS, privMap))} />
            <GrpBtn label="Special" checked={allEnabled(allVisibleScreens, SPL_KEYS, privMap)} partial={anyEnabled(allVisibleScreens, SPL_KEYS, privMap) && !allEnabled(allVisibleScreens, SPL_KEYS, privMap)} color={sHd}
              onClick={() => setGroupForScreens(allVisibleScreens, SPL_KEYS, !allEnabled(allVisibleScreens, SPL_KEYS, privMap))} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: cBg, width: COMMON_OPS.length * COL_W, fontSize: 9, fontWeight: 800, color: cHd, letterSpacing: '.12em', padding: '3px 0', borderLeft: `1px solid ${bd}` }}>
            COMMON
          </div>
          <div style={{ width: 2, background: bd, flexShrink: 0 }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: sBg, width: SPECIAL_OPS.length * COL_W, fontSize: 9, fontWeight: 800, color: sHd, letterSpacing: '.12em', padding: '3px 0' }}>
            SPECIAL
          </div>
        </div>
        {/* Column headers — vertical text + clickable to toggle that op for ALL screens */}
        <div style={{ display: 'flex', alignItems: 'flex-end', background: isDark ? '#1e2533' : '#f8fafc', paddingBottom: 4 }}>
          <div style={{ width: 200, flexShrink: 0, padding: '0 12px', fontSize: 10, fontWeight: 700, color: mt, letterSpacing: '.06em', alignSelf: 'center' }}>SCREEN</div>
          <div style={{ width: 80, flexShrink: 0, textAlign: 'center', fontSize: 9, color: mt, alignSelf: 'center', lineHeight: 1.2 }}>
            <div>ID</div>
            <div style={{ fontSize: 8, color: mt, opacity: .7 }}>click name = all ops</div>
          </div>
          {COMMON_OPS.map(op => {
            const colAll = allVisibleScreens.length > 0 && allVisibleScreens.every(s => privMap[s.screenId]?.[op.key]);
            return (
              <div key={op.key}
                title={`${op.label} — click to toggle ALL screens`}
                onClick={() => setGroupForScreens(allVisibleScreens, [op.key], !colAll)}
                style={{ width: COL_W, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', paddingBottom: 2, minHeight: 56, cursor: 'pointer', borderRadius: 3, transition: 'background .12s' }}
                onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = `${op.color}18`; }}
                onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 12, fontWeight: 800, color: colAll ? op.color : `${op.color}88`, userSelect: 'none', letterSpacing: '.03em' }}>{op.label}</span>
              </div>
            );
          })}
          <div style={{ width: 2, background: bd, alignSelf: 'stretch', flexShrink: 0 }} />
          {SPECIAL_OPS.map(op => {
            const colAll = allVisibleScreens.length > 0 && allVisibleScreens.every(s => privMap[s.screenId]?.[op.key]);
            return (
              <div key={op.key}
                title={`${op.label} — click to toggle ALL screens`}
                onClick={() => setGroupForScreens(allVisibleScreens, [op.key], !colAll)}
                style={{ width: COL_W, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', paddingBottom: 2, minHeight: 56, cursor: 'pointer', borderRadius: 3, transition: 'background .12s' }}
                onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.background = `${op.color}18`; }}
                onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 12, fontWeight: 800, color: colAll ? op.color : `${op.color}88`, userSelect: 'none', letterSpacing: '.03em' }}>{op.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tree body */}
      {tree.length === 0 ? (
        <div style={{ padding: '40px 20px', color: mt, textAlign: 'center', fontSize: 13 }}>
          No screens found. Module tree may still be loading.
        </div>
      ) : tree.map(mod => {
        const modScreens = mod.subModules.flatMap(sm => sm.screens);
        const modExp  = expandWork[mod.moduleId] !== false;
        const mAllC   = allEnabled(modScreens, COM_KEYS, privMap);
        const mAnyC   = anyEnabled(modScreens, COM_KEYS, privMap);
        const mAllS   = allEnabled(modScreens, SPL_KEYS, privMap);
        const mAnyS   = anyEnabled(modScreens, SPL_KEYS, privMap);

        return (
          <div key={mod.moduleId}>
            {/* Module row */}
            <div
              style={{ display: 'flex', alignItems: 'center', padding: '5px 12px', background: isDark ? '#1e2533' : '#e8edf5', borderBottom: `1px solid ${bd}`, cursor: 'pointer', minHeight: 32 }}
              onClick={() => setExpandWork(p => ({ ...p, [mod.moduleId]: !modExp }))}>
              <span style={{ width: 18, color: mt, flexShrink: 0 }}>{modExp ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</span>
              <span style={{ fontWeight: 800, fontSize: 12, color: tx, flex: 1 }}>{mod.moduleId}. {mod.moduleName}</span>
              <div style={{ display: 'flex', gap: 5, marginRight: 8 }}>
                <GrpBtn label="Common" checked={mAllC} partial={mAnyC && !mAllC} color={cHd}
                  onClick={e => { e.stopPropagation(); setGroupForScreens(modScreens, COM_KEYS, !mAllC); }} />
                <GrpBtn label="Special" checked={mAllS} partial={mAnyS && !mAllS} color={sHd}
                  onClick={e => { e.stopPropagation(); setGroupForScreens(modScreens, SPL_KEYS, !mAllS); }} />
              </div>
              <div style={{ width: TOTAL_W, flexShrink: 0 }} />
            </div>

            {modExp && mod.subModules.map(sm => {
              const smExp  = expandWork[sm.subModuleId] !== false;
              const sAllC  = allEnabled(sm.screens, COM_KEYS, privMap);
              const sAnyC  = anyEnabled(sm.screens, COM_KEYS, privMap);
              const sAllS  = allEnabled(sm.screens, SPL_KEYS, privMap);
              const sAnyS  = anyEnabled(sm.screens, SPL_KEYS, privMap);
              return (
                <div key={sm.subModuleId}>
                  {/* SubModule row */}
                  <div
                    style={{ display: 'flex', alignItems: 'center', padding: '4px 12px 4px 28px', background: isDark ? '#161b27' : '#f0f4ff', borderBottom: `1px solid ${bd}`, cursor: 'pointer', minHeight: 28 }}
                    onClick={() => setExpandWork(p => ({ ...p, [sm.subModuleId]: !smExp }))}>
                    <span style={{ width: 14, color: mt, flexShrink: 0 }}>{smExp ? <ChevronDown size={11} /> : <ChevronRight size={11} />}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: mt, flex: 1 }}>{sm.subModuleName}</span>
                    <div style={{ display: 'flex', gap: 4, marginRight: 8 }}>
                      <MiniBtn checked={sAllC} partial={sAnyC && !sAllC} color={cHd} label="All" title="All Common"
                        onClick={e => { e.stopPropagation(); setGroupForScreens(sm.screens, COM_KEYS, !sAllC); }} />
                      <MiniBtn checked={sAllS} partial={sAnyS && !sAllS} color={sHd} label="Spl" title="All Special"
                        onClick={e => { e.stopPropagation(); setGroupForScreens(sm.screens, SPL_KEYS, !sAllS); }} />
                    </div>
                    <div style={{ width: TOTAL_W, flexShrink: 0 }} />
                  </div>

                  {/* Screen rows */}
                  {smExp && sm.screens.map(s => {
                    const sAllOps = ALL_KEYS.every(k => getOp(s.screenId, k));
                    const sAnyOps = ALL_KEYS.some(k => getOp(s.screenId, k));
                    return (
                    <div key={s.screenId}
                      style={{ display: 'flex', alignItems: 'center', padding: '2px 12px 2px 44px', borderBottom: `1px solid ${bd}08`, minHeight: 27 }}
                      onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = rh; }}
                      onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'transparent'; }}>
                      {/* Screen name — click to toggle ALL ops for this screen */}
                      <div
                        onClick={() => setGroupForScreens([s], ALL_KEYS, !sAllOps)}
                        title={`Click to ${sAllOps ? 'remove all' : 'grant all'} permissions for this screen`}
                        style={{ width: 156, flexShrink: 0, fontSize: 11, color: sAllOps ? '#6366f1' : sAnyOps ? tx : mt, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', fontWeight: sAllOps ? 700 : sAnyOps ? 500 : 400, textDecoration: 'underline', textDecorationColor: 'transparent', textUnderlineOffset: 2, transition: 'color .12s' }}
                        onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.color = '#6366f1'; e.currentTarget.style.textDecorationColor = '#6366f1'; }}
                        onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.color = sAllOps ? '#6366f1' : sAnyOps ? tx : mt; e.currentTarget.style.textDecorationColor = 'transparent'; }}>
                        {s.screenTitle || s.screenId}
                      </div>
                      <div style={{ width: 80, flexShrink: 0, textAlign: 'center', fontSize: 10, color: mt }}>{s.screenId}</div>
                      {COMMON_OPS.map(op => (
                        <div key={op.key} style={{ width: COL_W, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            data-testid={`prof-${s.screenId}-${op.key}`}
                            checked={getOp(s.screenId, op.key)}
                            onChange={() => toggleOp(s.screenId, op.key)}
                            title={op.label}
                            style={{ accentColor: op.color, width: 13, height: 13, cursor: 'pointer' }}
                          />
                        </div>
                      ))}
                      <div style={{ width: 2, background: isDark ? '#2d3748' : '#e2e8f0', alignSelf: 'stretch', flexShrink: 0 }} />
                      {SPECIAL_OPS.map(op => (
                        <div key={op.key} style={{ width: COL_W, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            data-testid={`prof-${s.screenId}-${op.key}`}
                            checked={getOp(s.screenId, op.key)}
                            onChange={() => toggleOp(s.screenId, op.key)}
                            title={op.label}
                            style={{ accentColor: op.color, width: 13, height: 13, cursor: 'pointer' }}
                          />
                        </div>
                      ))}
                    </div>
                    );
                  })}
                  {smExp && !sm.screens.length && (
                    <div style={{ padding: '5px 12px 5px 44px', color: mt, fontSize: 10, fontStyle: 'italic' }}>No screens</div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function UserProfilesScreen({ theme }) {
  const isDark = theme === 'dark';

  const [profiles,         setProfiles]         = useState([]);
  const [selectedProfile,  setSelectedProfile]  = useState(null);
  const [privMap,          setPrivMap]           = useState({});
  const [moduleTree,       setModuleTree]        = useState([]);
  const [users,            setUsers]             = useState([]);
  const [loading,          setLoading]           = useState(false);
  const [treeLoading,      setTreeLoading]       = useState(false);
  const [saving,           setSaving]            = useState(false);
  const [applying,         setApplying]          = useState(false);
  const [isNew,            setIsNew]             = useState(false);
  const [newForm,          setNewForm]           = useState({ ProfileId: '', ProfileName: '', Description: '' });
  const [expandWork,       setExpandWork]        = useState({});
  const [applyUserId,      setApplyUserId]       = useState('');
  const [showApplyDrop,    setShowApplyDrop]     = useState(false);
  const [profileSearch,    setProfileSearch]     = useState('');
  const [toast,            setToast]             = useState(null);
  const applyRef = useRef(null);

  // ── Theme ─────────────────────────────────────────────────────────────────────
  const bg    = isDark ? '#0f1117' : '#f8fafc';
  const panel = isDark ? '#161b27' : '#ffffff';
  const bd    = isDark ? '#1e2533' : '#e2e8f0';
  const tx    = isDark ? '#e2e8f0' : '#1e293b';
  const mt    = isDark ? '#64748b' : '#94a3b8';
  const ac    = '#6366f1';
  const rh    = isDark ? '#1a2035' : '#f1f5f9';
  const cBg   = isDark ? '#1e2d3b' : '#eff6ff';
  const cHd   = isDark ? '#60a5fa' : '#1d4ed8';
  const sBg   = isDark ? '#291e3b' : '#fdf4ff';
  const sHd   = isDark ? '#c084fc' : '#7e22ce';

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Async helpers (defined BEFORE useEffect, called from click handlers only) ──

  const refreshProfiles = async () => {
    try {
      const d = await authFetch('/api/uam/profiles').then(r => r.json());
      if (d.success) setProfiles(d.data || []);
    } catch (_) {/**/}
  };

  const handleSelectProfile = async (p) => {
    setLoading(true);
    setIsNew(false);
    try {
      const d = await authFetch(`/api/uam/profiles/${p.ProfileId}`).then(r => r.json());
      if (d.success) {
        setSelectedProfile(d.data);
        setPrivMap(d.data.privMap || {});
      } else { showToast('Could not load profile', 'error'); }
    } catch (_) { showToast('Network error', 'error'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        if (!newForm.ProfileId.trim() || !newForm.ProfileName.trim()) {
          showToast('Profile ID and Name are required', 'error'); return;
        }
        const d = await authFetch('/api/uam/profiles', {
          method: 'POST',
          body: JSON.stringify({ ...newForm, privMap }),
        }).then(r => r.json());
        if (d.success) {
          showToast('Profile created');
          setIsNew(false);
          setNewForm({ ProfileId: '', ProfileName: '', Description: '' });
          await refreshProfiles();
        } else { showToast(d.error || 'Create failed', 'error'); }
      } else {
        if (!selectedProfile) { showToast('No profile selected', 'error'); return; }
        const d = await authFetch(`/api/uam/profiles/${selectedProfile.ProfileId}`, {
          method: 'PUT',
          body: JSON.stringify({ ProfileName: selectedProfile.ProfileName, Description: selectedProfile.Description, privMap }),
        }).then(r => r.json());
        if (d.success) { showToast('Profile saved'); await refreshProfiles(); }
        else { showToast(d.error || 'Save failed', 'error'); }
      }
    } catch (_) { showToast('Network error', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedProfile) return;
    if (selectedProfile.IsBuiltin) { showToast('Built-in profiles cannot be deleted', 'error'); return; }
    if (!window.confirm(`Delete profile "${selectedProfile.ProfileName}"?`)) return;
    try {
      const d = await authFetch(`/api/uam/profiles/${selectedProfile.ProfileId}`, { method: 'DELETE' }).then(r => r.json());
      if (d.success) {
        showToast('Profile deleted');
        setSelectedProfile(null);
        setPrivMap({});
        await refreshProfiles();
      } else { showToast(d.error || 'Delete failed', 'error'); }
    } catch (_) { showToast('Network error', 'error'); }
  };

  const handleApply = async () => {
    if (!applyUserId) { showToast('Select a user first', 'error'); return; }
    if (!selectedProfile) { showToast('Select a profile first', 'error'); return; }
    setApplying(true);
    try {
      const d = await authFetch(`/api/uam/profiles/${selectedProfile.ProfileId}/apply`, {
        method: 'POST',
        body: JSON.stringify({ userId: applyUserId }),
      }).then(r => r.json());
      if (d.success) {
        showToast(`Profile applied to user (${d.applied} screens)`);
        setShowApplyDrop(false);
        setApplyUserId('');
      } else { showToast(d.error || 'Apply failed', 'error'); }
    } catch (_) { showToast('Network error', 'error'); }
    finally { setApplying(false); }
  };

  // ── useEffect — initial data load, inline async (project ESLint pattern) ──────
  useEffect(() => {
    const init = async () => {
      setTreeLoading(true);
      try {
        const [profRes, treeRes, usersRes] = await Promise.all([
          authFetch('/api/uam/profiles').then(r => r.json()),
          authFetch('/api/uam/module-tree').then(r => r.json()),
          authFetch('/api/uam/users').then(r => r.json()),
        ]);
        if (profRes.success) setProfiles(profRes.data || []);
        if (treeRes.success && treeRes.data?.length) {
          setModuleTree(treeRes.data);
          setExpandWork({ [treeRes.data[0].moduleId]: true });
        }
        if (usersRes.success) setUsers(usersRes.data || []);
      } catch (_) {/**/} finally { setTreeLoading(false); }
    };
    init();
  }, []);

  // Outside-click dismisses Apply dropdown — stable effect (applyRef guards null)
  useEffect(() => {
    const handler = (e) => {
      if (applyRef.current && !applyRef.current.contains(e.target)) setShowApplyDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Privilege helpers ─────────────────────────────────────────────────────────
  const toggleOp = (sid, op) => {
    setPrivMap(prev => {
      const cur = prev[sid] || {};
      const next = { ...cur, [op]: !cur[op] };
      if (op === 'visible' && !next.visible) return { ...prev, [sid]: ALL_KEYS.reduce((a, k) => ({ ...a, [k]: false }), {}) };
      if (op !== 'visible' && next[op] && !next.visible) next.visible = true;
      return { ...prev, [sid]: next };
    });
  };

  const setGroupForScreens = (screens, keys, checked) => {
    setPrivMap(prev => {
      const n = { ...prev };
      screens.forEach(s => {
        n[s.screenId] = { ...(n[s.screenId] || {}), ...keys.reduce((a, k) => ({ ...a, [k]: checked }), {}) };
        if (checked && !n[s.screenId].visible) n[s.screenId].visible = true;
      });
      return n;
    });
  };

  // ── Derived ───────────────────────────────────────────────────────────────────
  const filteredProfiles = profileSearch
    ? profiles.filter(p => p.ProfileName.toLowerCase().includes(profileSearch.toLowerCase()) || p.ProfileId.toLowerCase().includes(profileSearch.toLowerCase()))
    : profiles;

  const globalProfiles = filteredProfiles.filter(p => p.IsBuiltin || !p.ClientId);
  const tenantProfiles = filteredProfiles.filter(p => !p.IsBuiltin && p.ClientId);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div
      data-testid="user-profiles-screen"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: bg, fontFamily: 'var(--erp-font, Inter, sans-serif)', fontSize: 13 }}
    >
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999, padding: '10px 18px', borderRadius: 8, background: toast.type === 'error' ? '#fee2e2' : '#dcfce7', color: toast.type === 'error' ? '#991b1b' : '#166534', fontSize: 13, fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,.12)' }}>
          {toast.msg}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: panel, borderBottom: `1px solid ${bd}`, flexShrink: 0 }}>
        <Shield size={15} color={ac} />
        <span style={{ fontWeight: 700, fontSize: 13, color: tx }}>User Profiles</span>
        <div style={{ flex: 1 }} />

        {/* Apply to User */}
        <div ref={applyRef} style={{ position: 'relative' }}>
          <button
            data-testid="apply-profile-btn"
            onClick={() => setShowApplyDrop(o => !o)}
            disabled={!selectedProfile}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', border: `1px solid ${selectedProfile ? '#93c5fd' : bd}`, borderRadius: 5, background: selectedProfile ? '#eff6ff' : (isDark ? '#1a2035' : '#f8fafc'), color: selectedProfile ? '#1d4ed8' : mt, fontSize: 12, fontWeight: 600, cursor: selectedProfile ? 'pointer' : 'default' }}
          >
            <Users size={13} /> Apply to User
          </button>
          {showApplyDrop && (
            <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 2000, background: panel, border: `1px solid ${bd}`, borderRadius: 6, boxShadow: '0 6px 24px rgba(0,0,0,.15)', padding: 12, minWidth: 260 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: mt, marginBottom: 6 }}>Apply &ldquo;{selectedProfile?.ProfileName}&rdquo; to:</div>
              <select
                data-testid="apply-user-select"
                value={applyUserId}
                onChange={e => setApplyUserId(e.target.value)}
                style={{ width: '100%', padding: '5px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, marginBottom: 8 }}
              >
                <option value="">-- Select User --</option>
                {users.map(u => <option key={u.UserId} value={u.UserId}>{u.UserName} ({u.UserId})</option>)}
              </select>
              <button
                data-testid="confirm-apply-btn"
                onClick={handleApply}
                disabled={applying || !applyUserId}
                style={{ width: '100%', padding: '6px', background: applying || !applyUserId ? '#a5b4fc' : ac, color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: applying || !applyUserId ? 'default' : 'pointer' }}
              >
                {applying ? 'Applying...' : 'Apply Profile'}
              </button>
            </div>
          )}
        </div>

        {/* New Profile */}
        <button
          data-testid="new-profile-btn"
          onClick={() => { setIsNew(true); setSelectedProfile(null); setPrivMap({}); setNewForm({ ProfileId: '', ProfileName: '', Description: '' }); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: '#f0fdf4', border: `1px solid #86efac`, color: '#166534', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          <Plus size={13} /> New Profile
        </button>

        {/* Save */}
        <button
          data-testid="save-profile-btn"
          onClick={handleSave}
          disabled={saving || (!isNew && !selectedProfile)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: saving ? '#a5b4fc' : (!isNew && !selectedProfile ? (isDark ? '#1e2533' : '#f1f5f9') : ac), color: (!isNew && !selectedProfile) ? mt : '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: saving || (!isNew && !selectedProfile) ? 'default' : 'pointer' }}
        >
          <Save size={13} /> {saving ? 'Saving...' : 'Save Profile'}
        </button>

        {/* Refresh */}
        <button
          data-testid="refresh-profiles-btn"
          onClick={() => refreshProfiles()}
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', border: `1px solid ${bd}`, borderRadius: 5, background: 'transparent', color: mt, fontSize: 12, cursor: 'pointer' }}
        >
          <RefreshCw size={12} />
        </button>
      </div>

      {/* New Profile creation bar */}
      {isNew && (
        <div style={{ padding: '8px 14px', background: isDark ? '#1e2533' : '#fffbeb', borderBottom: `1px solid ${bd}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '.06em' }}>New Profile</span>
          <input
            data-testid="new-profile-id-input"
            placeholder="Profile ID *"
            value={newForm.ProfileId}
            onChange={e => setNewForm(p => ({ ...p, ProfileId: e.target.value.toUpperCase().replace(/\s/g, '_') }))}
            style={{ padding: '4px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, width: 140 }}
          />
          <input
            data-testid="new-profile-name-input"
            placeholder="Profile Name *"
            value={newForm.ProfileName}
            onChange={e => setNewForm(p => ({ ...p, ProfileName: e.target.value }))}
            style={{ padding: '4px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, width: 210 }}
          />
          <input
            data-testid="new-profile-desc-input"
            placeholder="Description"
            value={newForm.Description}
            onChange={e => setNewForm(p => ({ ...p, Description: e.target.value }))}
            style={{ padding: '4px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, flex: 1, minWidth: 150 }}
          />
          <button
            onClick={() => setIsNew(false)}
            style={{ padding: '4px 10px', border: `1px solid ${bd}`, borderRadius: 4, background: 'transparent', color: mt, fontSize: 11, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Main area: profile list (left) + matrix (right) */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

        {/* Left: Profile List */}
        <div style={{ width: 248, flexShrink: 0, borderRight: `1px solid ${bd}`, background: panel, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Search */}
          <div style={{ padding: '8px 10px', borderBottom: `1px solid ${bd}`, flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={11} style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: mt }} />
              <input
                data-testid="profile-search-input"
                placeholder="Search profiles..."
                value={profileSearch}
                onChange={e => setProfileSearch(e.target.value)}
                style={{ width: '100%', padding: '4px 8px 4px 24px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 11, background: isDark ? '#1e2533' : '#f8fafc', color: tx, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Profile list */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {/* Global profiles section */}
            {globalProfiles.length > 0 && (
              <>
                <div style={{ padding: '5px 10px 3px', fontSize: 9, fontWeight: 800, color: '#92400e', letterSpacing: '.1em', textTransform: 'uppercase', background: isDark ? '#1a1a00' : '#fef9c3' }}>
                  GLOBAL / BUILT-IN
                </div>
                {globalProfiles.map(p => (
                  <ProfileListItem
                    key={p.ProfileId}
                    profile={p}
                    isSelected={selectedProfile?.ProfileId === p.ProfileId}
                    onClick={() => handleSelectProfile(p)}
                    isDark={isDark}
                    ac={ac}
                    tx={tx}
                    mt={mt}
                    bd={bd}
                    loading={loading}
                  />
                ))}
              </>
            )}

            {/* Tenant profiles section */}
            {tenantProfiles.length > 0 && (
              <>
                <div style={{ padding: '5px 10px 3px', fontSize: 9, fontWeight: 800, color: '#1d4ed8', letterSpacing: '.1em', textTransform: 'uppercase', background: isDark ? '#001a2e' : '#eff6ff' }}>
                  TENANT PROFILES
                </div>
                {tenantProfiles.map(p => (
                  <ProfileListItem
                    key={p.ProfileId}
                    profile={p}
                    isSelected={selectedProfile?.ProfileId === p.ProfileId}
                    onClick={() => handleSelectProfile(p)}
                    isDark={isDark}
                    ac={ac}
                    tx={tx}
                    mt={mt}
                    bd={bd}
                    loading={loading}
                  />
                ))}
              </>
            )}

            {filteredProfiles.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: mt, fontSize: 12 }}>
                {profileSearch ? 'No profiles match your search' : 'No profiles available'}
              </div>
            )}
          </div>
        </div>

        {/* Right: Matrix or placeholder */}
        {(selectedProfile || isNew) ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            {/* Selected profile meta bar */}
            {selectedProfile && !isNew && (
              <div style={{ padding: '6px 14px', background: isDark ? '#1a2035' : '#f8fafc', borderBottom: `1px solid ${bd}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <Shield size={13} color={selectedProfile.IsBuiltin ? '#92400e' : ac} />
                <input
                  data-testid="profile-name-inline-edit"
                  value={selectedProfile.ProfileName}
                  onChange={e => setSelectedProfile(p => ({ ...p, ProfileName: e.target.value }))}
                  readOnly={!!selectedProfile.IsBuiltin}
                  title={selectedProfile.IsBuiltin ? 'Built-in profile name is read-only' : 'Click to edit name'}
                  style={{ fontWeight: 700, fontSize: 13, color: tx, background: 'transparent', border: 'none', outline: 'none', flex: 1, cursor: selectedProfile.IsBuiltin ? 'default' : 'text' }}
                />
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: selectedProfile.IsBuiltin ? '#fef9c3' : '#eff6ff', color: selectedProfile.IsBuiltin ? '#92400e' : '#1d4ed8', fontWeight: 700, flexShrink: 0 }}>
                  {selectedProfile.IsBuiltin ? 'GLOBAL' : 'TENANT'}
                </span>
                {!selectedProfile.IsBuiltin && (
                  <button
                    data-testid="delete-profile-btn"
                    onClick={handleDelete}
                    style={{ padding: '3px 8px', background: '#fee2e2', color: '#991b1b', border: `1px solid #fca5a5`, borderRadius: 4, fontSize: 11, cursor: 'pointer' }}
                  >
                    <Trash2 size={11} />
                  </button>
                )}
                <div style={{ fontSize: 10, color: mt }}>
                  {selectedProfile.Description || '—'}
                </div>
              </div>
            )}

            {/* Info bar when matrix is empty */}
            {!treeLoading && Object.keys(privMap).length === 0 && (selectedProfile || isNew) && (
              <div style={{ padding: '5px 14px', background: isDark ? '#1a2035' : '#fffbeb', borderBottom: `1px solid ${bd}`, fontSize: 11, color: '#92400e', flexShrink: 0 }}>
                No privileges configured yet — use the matrix below to set permissions, then click Save Profile.
              </div>
            )}

            {/* Matrix */}
            {treeLoading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: mt }}>
                Loading module tree...
              </div>
            ) : (
              <ProfilePrivMatrix
                tree={moduleTree}
                privMap={privMap}
                expandWork={expandWork}
                setExpandWork={setExpandWork}
                toggleOp={toggleOp}
                setGroupForScreens={setGroupForScreens}
                isDark={isDark}
                panel={panel}
                bd={bd}
                tx={tx}
                mt={mt}
                rh={rh}
                cBg={cBg}
                cHd={cHd}
                sBg={sBg}
                sHd={sHd}
              />
            )}
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: mt }}>
            <Shield size={36} color={bd} />
            <div style={{ fontSize: 14, fontWeight: 600, color: mt }}>Select a profile to edit privileges</div>
            <div style={{ fontSize: 12 }}>or click &ldquo;New Profile&rdquo; to create one</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Profile List Item sub-component ───────────────────────────────────────────
function ProfileListItem({ profile, isSelected, onClick, isDark, ac, tx, mt, bd, loading }) {
  return (
    <div
      data-testid={`profile-item-${profile.ProfileId}`}
      onClick={!loading ? onClick : undefined}
      style={{
        padding: '8px 10px',
        cursor: loading ? 'default' : 'pointer',
        background: isSelected ? (isDark ? '#1e3a5f' : '#eff6ff') : 'transparent',
        borderBottom: `1px solid ${bd}22`,
        borderLeft: isSelected ? `3px solid ${ac}` : '3px solid transparent',
        opacity: loading ? 0.6 : 1,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: isSelected ? ac : tx }}>{profile.ProfileName}</div>
      <div style={{ fontSize: 10, color: mt, marginTop: 1 }}>{profile.ProfileId}</div>
    </div>
  );
}
